(() => {
  if (customElements.get('shady-interior')) return;

  const lines = text => [
    ...new Set(
      String(text || '')
        .split(/\r?\n/)
        .map(value => value.trim())
        .filter(Boolean)
    )
  ];

  const norm = text => String(text || '').trim().toLowerCase();

  const packages = body =>
    body === 'Ute'
      ? ['Front seats only', 'Complete Interior']
      : ['Front seats only', 'Front and rear seats', 'Complete Interior'];

  class ShadyInterior extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      this.config = JSON.parse(this.dataset.config);
      this.controls = {};
      this.labels = {};
      this.sequence = 0;
      this.busy = false;

      this.form = this.querySelector('form');
      this.submit = this.querySelector('.si-submit');
      this.status = this.querySelector('.si-status');
      this.error = this.querySelector('.si-error');

      this.config.vehicles = this.config.vehicles.filter(
        vehicle =>
          vehicle.model &&
          vehicle.bodies.some(body => body.enabled)
      );

      const fields = [
        ['vehicle', 'make', 'Make'],
        ['vehicle', 'model', 'Model'],
        ['vehicle', 'body', 'Body shape'],
        ['vehicle', 'package', 'Package'],
        ['style', 'foam', 'Seat foam style'],
        ['material', 'face', 'Seat face material'],
        ['material', 'back', 'Back + sides material'],
        ['material', 'colour', 'Material colour'],
        ['material', 'stitch', 'Stitching colour'],
        ['embroidery', 'seat', 'Seat embroidery'],
        ['embroidery', 'dash', 'VF dash embroidery']
      ];

      for (const [group, key, label] of fields) {
        const wrap = document.createElement('label');
        wrap.className = 'si-field';

        const caption = document.createElement('span');
        caption.textContent = label;

        const select = document.createElement('select');
        select.name = `properties[${label}]`;
        select.required = true;
        select.dataset.key = key;

        wrap.append(caption, select);

        this.querySelector(
          `[data-group="${group}"]`
        ).append(wrap);

        this.controls[key] = select;
        this.labels[key] = label;
      }

      this.fill('face', ['Alcantara', 'Vinyl Leather']);
      this.fill('back', ['Alcantara', 'Vinyl Leather']);
      this.fill('colour', lines(this.config.colours));
      this.fill('stitch', lines(this.config.stitches));

      this.fill(
        'make',
        [...new Set(this.config.vehicles.map(vehicle => vehicle.make))]
      );

      for (const vehicle of this.config.vehicles) {
        const body = vehicle.bodies.find(
          body =>
            body.enabled &&
            body.handle &&
            body.handle === this.config.currentHandle
        );

        if (body) {
          this.initialVehicle = {
            model: vehicle.model,
            body: body.name
          };

          this.controls.make.value = vehicle.make;
          break;
        }
      }

      this.buildPatterns();
      this.updateModels();

      this.form.addEventListener('change', event => {
        if (this.busy) return;

        this.error.textContent = '';

        const key = event.target.dataset.key;

        if (key === 'make') {
          this.updateModels();
        } else if (key === 'model' || key === 'package') {
          this.updateBodies();
        } else if (key === 'body') {
          this.updatePackages();
        } else {
          this.refresh();
        }
      });

      this.form.addEventListener(
        'submit',
        event => this.addToCart(event)
      );
    }

    fill(key, values) {
      const select = this.controls[key];
      const previousValue = select.value;

      select.replaceChildren();

      for (const value of values) {
        select.add(new Option(value, value));
      }

      if (values.includes(previousValue)) {
        select.value = previousValue;
      }
    }

    value(key) {
      return this.controls[key].value;
    }

    vehicle() {
      return this.config.vehicles.find(
        vehicle =>
          vehicle.make === this.value('make') &&
          vehicle.model === this.value('model')
      );
    }

    route() {
      return this.vehicle()?.bodies.find(
        body =>
          body.enabled &&
          body.name === this.value('body')
      );
    }

    updateModels() {
      this.fill(
        'model',
        this.config.vehicles
          .filter(vehicle => vehicle.make === this.value('make'))
          .map(vehicle => vehicle.model)
      );

      if (this.initialVehicle) {
        this.controls.model.value = this.initialVehicle.model;
      }

      const prefix =
        this.value('make') === 'Holden' ? 'holden' : 'ford';

      this.fill(
        'foam',
        lines(this.config[prefix + 'Foam'])
      );

      this.fill(
        'seat',
        lines(this.config[prefix + 'Embroidery'])
      );

      this.updateBodies();
    }

    updateBodies() {
      this.fill(
        'body',
        (this.vehicle()?.bodies || [])
          .filter(body => body.enabled)
          .map(body => body.name)
      );

      if (this.initialVehicle) {
        this.controls.body.value = this.initialVehicle.body;
        this.initialVehicle = null;
      }

      const showDash =
        this.value('make') === 'Holden' &&
        norm(this.value('model')) === 'vf' &&
        this.value('package') === 'Complete Interior';

      this.controls.dash.parentElement.hidden = !showDash;
      this.controls.dash.disabled = !showDash;

      // Remove the dash selection when it is no longer applicable.
      this.fill(
        'dash',
        showDash ? lines(this.config.dashEmbroidery) : []
      );

      this.updatePackages();
    }

    updatePackages() {
      this.fill(
        'package',
        packages(this.value('body'))
      );

      this.loadProduct();
    }

    buildPatterns() {
      const target = this.querySelector('[data-patterns]');

      const title = document.createElement('p');
      title.textContent = 'Stitching pattern';

      const grid = document.createElement('div');
      grid.className = 'si-pattern-grid';

      this.config.patterns.forEach((pattern, index) => {
        const card = document.createElement('label');
        card.className = 'si-pattern';

        const input = document.createElement('input');
        input.type = 'radio';
        input.name = 'properties[Stitching pattern]';
        input.value = pattern.name;
        input.required = true;
        input.checked = index === 0;

        const content = document.createElement('span');
        content.className = 'si-pattern-content';

        if (pattern.image) {
          const image = document.createElement('img');
          image.src = pattern.image;
          image.alt = pattern.name;
          image.loading = 'lazy';

          content.append(image);
        }

        const name = document.createElement('span');
        name.textContent = pattern.name;

        content.append(name);
        card.append(input, content);
        grid.append(card);
      });

      target.append(title, grid);
    }

    properties() {
      const properties = {};

      for (const [key, control] of Object.entries(this.controls)) {
        if (!control.parentElement.hidden && control.value) {
          properties[this.labels[key]] = control.value;
        }
      }

      const pattern = this.querySelector(
        'input[type="radio"]:checked'
      );

      if (pattern) {
        properties['Stitching pattern'] = pattern.value;
      }

      return properties;
    }

    async loadProduct() {
      const sequence = ++this.sequence;

      this.product = null;
      this.loadError = '';
      this.loading = true;

      this.refresh();

      const handle = this.route()?.handle;

      if (!handle) {
        this.loading = false;

        this.loadError =
          'Pricing is not yet available for this vehicle. Please enquire.';

        this.refresh();
        return;
      }

      try {
        const root = window.Shopify?.routes?.root || '/';

        const response = await fetch(
          `${root}products/${encodeURIComponent(handle)}.js`,
          {
            headers: {
              Accept: 'application/json'
            },
            cache: 'no-store'
          }
        );

        if (!response.ok) {
          throw new Error(
            'This interior is currently unavailable. Please enquire.'
          );
        }

        const product = await response.json();

        if (
          sequence !== this.sequence ||
          !this.isConnected
        ) {
          return;
        }

        const optionNames = (product.options || []).map(
          option =>
            norm(
              typeof option === 'string'
                ? option
                : option.name
            )
        );

        if (
          optionNames.length !== 2 ||
          !optionNames.includes('package') ||
          !optionNames.includes('material colour')
        ) {
          throw new Error(
            'Pricing is being updated for this interior. Please enquire.'
          );
        }

        if (
          !Array.isArray(product.variants) ||
          product.variants.length >= 250
        ) {
          throw new Error(
            'This interior needs a pricing update. Please enquire.'
          );
        }

        product.packageIndex = optionNames.indexOf('package');
        product.colourIndex = optionNames.indexOf('material colour');

        this.product = product;
      } catch (error) {
        if (sequence !== this.sequence) return;

        this.loadError =
          error.message ||
          'Unable to load pricing. Please refresh and try again.';
      } finally {
        if (sequence === this.sequence) {
          this.loading = false;
          this.refresh();
        }
      }
    }

    variant() {
      const product = this.product;

      if (!product) return null;

      return product.variants.find(
        variant =>
          norm(variant.options[product.packageIndex]) ===
            norm(this.value('package')) &&
          norm(variant.options[product.colourIndex]) ===
            norm(this.value('colour'))
      );
    }

    validSelections() {
      return (
        Object.values(this.controls).every(
          control =>
            control.parentElement.hidden ||
            !!control.value
        ) &&
        !!this.querySelector('input[type="radio"]:checked')
      );
    }

    canPurchase() {
      const variant = this.variant();

      return (
        this.config.enabled &&
        !this.loading &&
        !this.loadError &&
        this.validSelections() &&
        variant?.available &&
        Number.isFinite(variant.price) &&
        variant.price > 0
      );
    }

    refresh() {
      const summary = this.querySelector('[data-summary]');

      summary.replaceChildren();

      for (const [key, value] of Object.entries(this.properties())) {
        const title = document.createElement('dt');
        const detail = document.createElement('dd');

        title.textContent = key;
        detail.textContent = value;

        summary.append(title, detail);
      }

      const variant = this.variant();

      this.querySelector('[data-price]').textContent =
        variant?.price > 0
          ? new Intl.NumberFormat(
              document.documentElement.lang || 'en-AU',
              {
                style: 'currency',
                currency: this.config.currency,
                currencyDisplay: 'code'
              }
            ).format(variant.price / 100)
          : 'Price on enquiry';

      this.status.textContent = this.loading
        ? 'Loading your price…'
        : this.loadError
          ? this.loadError
          : !this.config.enabled
            ? 'Online ordering is coming soon. Please enquire about your build.'
            : !this.validSelections()
              ? 'Please complete all your selections.'
              : !variant || !(variant.price > 0)
                ? 'Pricing is not yet available for this combination. Please enquire.'
                : !variant.available
                  ? 'This combination is currently unavailable. Please enquire.'
                  : 'Your selections will be saved with your order.';

      this.submit.disabled =
        this.busy || !this.canPurchase();
    }

    async addToCart(event) {
      event.preventDefault();

      if (
        this.busy ||
        !this.canPurchase() ||
        !this.form.reportValidity()
      ) {
        return;
      }

      this.error.textContent = '';
      this.busy = true;

      const variant = this.variant();
      const properties = this.properties();

      // Shopify's variant price is the full price charged.
      // Include the VF Complete premium in that price in Shopify.
      const inputs = [
        ...this.querySelectorAll('select,input,button')
      ];

      const disabledStates = inputs.map(
        element => element.disabled
      );

      inputs.forEach(element => {
        element.disabled = true;
      });

      this.submit.textContent = 'Adding your interior…';

      try {
        const root = window.Shopify?.routes?.root || '/';

        const response = await fetch(`${root}cart/add.js`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json'
          },
          body: JSON.stringify({
            items: [
              {
                id: variant.id,
                quantity: 1,
                properties
              }
            ]
          })
        });

        const result = await response.json();

        if (!response.ok) {
          throw new Error(
            result.description ||
            result.message ||
            'Unable to add this interior. Please try again.'
          );
        }

        window.location.assign(
          this.config.cartUrl || '/cart'
        );
      } catch (error) {
        this.error.textContent =
          `${error.message || 'Connection interrupted.'} ` +
          'If your connection dropped, check your cart before trying again.';

        this.busy = false;

        inputs.forEach((element, index) => {
          element.disabled = disabledStates[index];
        });

        this.submit.textContent = 'Add interior to cart';

        this.refresh();
      }
    }
  }

  customElements.define('shady-interior', ShadyInterior);
})();